import { Member, User } from '@citruscamps/citrus-client'
import { faExternalLink, faGift, faSignOut } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { default as React, useState } from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '../../../constants/routes'
import { useFetchUserMembers } from '../../../hooks/useFetchUserMember'
import { useRouter } from '../../../hooks/useRouter'
import { formatFullName } from '../../../utils/formatters'
import { Icon } from '../../Icon/Icon'
import { Popover } from '../../Popover/Popover'
import { useHoverPopper } from '../../Tooltip/hooks/useHoverPopper'
import { TruncatedText } from '../../TruncatedText/TruncatedText'
import { useFetchCitrusPromotion } from '../hooks/useFetchCitrusPromotions'
import { ReferralModal } from './ReferralModal'

interface IProps {
  user: User
  member?: Member
  setProgramId: React.Dispatch<React.SetStateAction<string | undefined>>
  isMobile?: boolean
}

export const UserDropdown = ({
  user,
  member,
  setProgramId,
  isMobile,
}: IProps): React.ReactElement => {
  const router = useRouter()
  const { data: members } = useFetchUserMembers({})
  const [isReferralModalOpen, setIsReferralModalOpen] = useState<boolean>()
  const { target, toggle, targetProps, ...tooltipProps } = useHoverPopper<HTMLButtonElement>({
    roleType: 'menu',
    placement: isMobile ? 'bottom-end' : 'right-end',
  })

  const program = member?.program
  const fullname = member ? formatFullName(member) : formatFullName(user)
  const picture_url = member ? member?.picture_url : user?.picture_url
  const { data: citrusPromo } = useFetchCitrusPromotion({ program })
  const { referral } = citrusPromo || {}
  const referalAmount: string | undefined = referral?.display?.split('.').shift()
  return (
    <>
      <button
        id="sidebarIcon"
        className="btn dropdown-toggle px-md-2 px-0 w-100 text-start"
        type="button"
        ref={target}
        {...targetProps}
        onClick={() => {
          if (!isMobile) {
            router.push(ROUTES.SETTINGS)
          } else {
            toggle()
          }
        }}
        style={{ textTransform: 'unset' }}
      >
        <TruncatedText>
          <div className="avatar avatar-sm">
            <Icon
              src={picture_url}
              className="avatar-img rounded shadow-sm border"
              alt={fullname}
            />
          </div>
          {!isMobile && <span className="ms-2">{fullname}</span>}
        </TruncatedText>
      </button>
      <Popover {...tooltipProps} toggle={toggle}>
        <Link
          className={`dropdown-item${(members?.length || 0) === 0 ? ' disabled' : ''}`}
          to={ROUTES.SETTINGS}
        >
          <div className="d-flex w-100">
            <Icon
              src={picture_url}
              className="avatar-img avatar-sm rounded my-auto shadow-sm"
              alt={fullname}
            />
            <div className="float-start my-auto ms-2">
              <TruncatedText className="fw-bold text-decoration-none h6 mt-2 mb-0" title={fullname}>
                {fullname}
              </TruncatedText>
              <TruncatedText
                className="text-decoration-none text-body-secondary"
                title={user.email}
              >
                {user.email}
              </TruncatedText>
              <div className="small text-primary">Citrus Settings</div>
            </div>
          </div>
        </Link>
        {member && (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`${process.env.REACT_APP_REGISTER_URL}/pg/${member.program.id}`}
            className="dropdown-item border-top"
          >
            <Icon
              src={member.program.picture_url}
              alt={member.program.name}
              className="program-brand-img bg-white rounded-circle me-3 border shadow-sm"
              style={{ height: '1.125rem', width: '1.125rem' }}
            />
            View public storefront
            <FontAwesomeIcon icon={faExternalLink} className="text-primary ms-2" />
          </a>
        )}
        {members && members.length > 1 && (
          <h6
            className="dropdown-header border-top"
            style={{
              '--bs-dropdown-header-padding-y': '1rem',
              '--bs-dropdown-header-padding-x': '1rem 0',
            }}
          >
            Switch to
          </h6>
        )}
        <div style={{ maxHeight: '10.5rem', overflow: 'auto' }}>
          {members?.map((m) =>
            m.id !== member?.id ? (
              <button
                type="button"
                className="dropdown-item"
                key={`select-${m.id}`}
                onClick={() => {
                  if (m.id !== member?.id) {
                    setProgramId(m.program.id)
                  }
                  toggle()
                }}
              >
                <TruncatedText>
                  <Icon
                    src={m.program.picture_url}
                    alt={m.program.name}
                    className="program-brand-img bg-white rounded-circle me-3 border shadow-sm"
                    style={{ height: '1.125rem', width: '1.125rem' }}
                  />
                  <span className="text-ellipsis align-middle">{m.program.name}</span>
                </TruncatedText>
              </button>
            ) : null,
          )}
        </div>
        <button
          type="button"
          className="btn dropdown-item border-top"
          onClick={() => {
            setIsReferralModalOpen(true)
            toggle()
          }}
        >
          <div className="d-flex">
            <FontAwesomeIcon icon={faGift} className="me-3 my-auto text-primary" />
            <div>
              <div>Invite other programs</div>
              <div className="small text-body-secondary" style={{ fontSize: '0.75rem' }}>
                You get {referalAmount} off
              </div>
            </div>
          </div>
        </button>
        {isReferralModalOpen && program && (
          <ReferralModal program={program} onClose={() => setIsReferralModalOpen(false)} />
        )}
        <Link className="dropdown-item border-top" to={ROUTES.LOGOUT}>
          <FontAwesomeIcon className="me-3" fixedWidth={true} icon={faSignOut} />
          Logout
        </Link>
      </Popover>
    </>
  )
}
